import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { MbsPopupType, ModalComponent, ModalService, ModalSettings } from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { finalize, noop } from 'rxjs';

enum PeriodVariants {
  WasabiSmall = 30,
  WasabiBig = 90
}

@UntilDestroy()
@Component({
  templateUrl: './minimum-storage-duration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MinimumStorageDurationComponent implements OnInit {
  public readonly periodVariants = PeriodVariants;
  public readonly storageTypes = StorageType;
  public readonly mbsPopupType = MbsPopupType;
  public storageType: StorageType;
  public storageAccountId: string;
  public durationControl = new FormControl(null);
  public loading = false;

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  @ViewChild('warningModalTemplate', { static: true }) private warningModalTemplate: TemplateRef<any>;

  constructor(
    public ability: AbilityService,
    private cdr: ChangeDetectorRef,
    private storageAccounts: StorageAccountsService,
    private modalService: ModalService,
    private i18N: I18NextPipe
  ) {}

  ngOnInit(): void {
    this.storageAccountId = this.baseModal.data.storageAccountId;
    this.storageType = this.baseModal.data.storageType;
    this.loading = true;
    this.storageAccounts
      .getMinimumStorageDurationForWasabi(this.storageAccountId)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();

          this.durationControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
            val === PeriodVariants.WasabiSmall && this.showWarningModal();
          });
        })
      )
      .subscribe((result: { duration: number }) => {
        this.durationControl.patchValue(result?.duration ? result.duration : PeriodVariants.WasabiBig);
      });
  }

  showWarningModal() {
    const settings: ModalSettings = {
      header: {
        title: this.i18N.transform('shared:minimalStorageDuration.title')
      },
      footer: {
        okButton: {
          text: this.i18N.transform('shared:buttons.confirm')
        }
      }
    };

    this.modalService
      .open(settings, this.warningModalTemplate)
      .then((res) => {
        res && this.selectClickHandler();
      })
      .catch(noop);
  }

  selectClickHandler(): void {
    if (this.storageType === StorageType.Wasabi) {
      this.loading = true;
      this.cdr.detectChanges();
      this.storageAccounts
        .minimumStorageDurationForWasabi(this.storageAccountId, this.durationControl.value)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            this.loading = false;
            this.baseModal.save(true);
          },
          error: () => {
            this.loading = false;
            this.baseModal.close();
          }
        });
    }
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }
}
